import ko from "knockout";
import _ from "lodash";
import ViewModel from "viewModels/ViewModel";
import qualityManagementService from "services/qualityManagementService";
import errorCallbackFactory from "util/errorCallbackFactory";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";
import LOG from "services/loggingService";
import AgentSelectorView from "viewModels/policies/editors/AgentSelectorView";
import ServiceResponseValidator from "viewModels/util/ServiceResponseValidator";
import { withRequestRetry } from "util/withRequestRetry";
import moment from "moment";
import { FULL_DATE_SHORT_TIME_OPTIONS } from "util/DateTimeHelper";
import Icons from "enums/Icons";

const AuditView = ViewModel.extend(function (
    conversationObservable,
    recordingView,
    conversationId
) {
    const self = this;

    self.Icons = Icons;

    self.isFlareThemeEnabled = featureToggleService.isEnabled(
        FeatureToggle.WEM_1418_IDUI_SPARK_V4_FLARE
    );

    self.recording = recordingView;
    self.auditGrid = ko.observable();
    self.selectedAuditEntityType = ko.observable();
    self.rows = ko.observableArray();
    self.showUserFilter = ko.observable(false);
    self.showLoadingSpinner = ko.observable(false);
    self.isUserFilterChecked = ko.observable(false);
    self.isTrusteeFilterChecked = ko.observable(false);
    self.currentPageNo = ko.observable(0);
    self.hasNextPage = ko.observable(false);
    self.selectedEntities = ko.observableArray([]);
    self.selectedActions = ko.observableArray([]);

    self.entitySelectAll = ko.observable(false);
    self.actionSelectAll = ko.observable(false);

    self.showDataTable = ko.observable(false);
    self.showLoadAllAudit = ko.observable(true);

    self.entityTypes = ko.observableArray([]);
    self.actionTypes = ko.observableArray([]);

    self.conversation = self.computed(function () {
        return ko.toJS(conversationObservable);
    });

    const setElementProperties = function (element, property, value) {
        if (element) {
            element[property] = value;
        }
    };

    const filterProperties = Object.freeze({
        USER_ID: "userId",
        ACTION: "action",
        ENTITY_TYPE: "entityType",
        CONVERSATION_ID: "conversationId",
        TRUSTEE_ONLY_AUDITS: "trusteeOnlyAudits",
    });

    const SORTING_ORDER = [{ name: "Timestamp", sortOrder: "descending" }];
    const responseValidator = new ServiceResponseValidator();
    self.users = ko
        .observableArray([])
        .extend({ responseValidator: responseValidator });

    const userOptions = {
        agentValidation: {
            maxLength: {
                params: 10,
                message: "agentSelectorView.maxUsersSelected",
            },
            minLength: {
                params: 1,
                message: "agentSelectorView.noAgentsSelected",
            },
        },
        userStates: ["active", "inactive", "deleted"],
    };
    self.userSelector = self.subView(
        AgentSelectorView,
        self.users,
        userOptions
    );

    self.disableSearch = self.computed(function () {
        return (
            self.showLoadingSpinner() ||
            self.users().length > 10 ||
            self.conversation() === null ||
            (self.selectedEntities().length === 0 &&
                !self.isTrusteeFilterChecked() &&
                !self.isUserFilterChecked()) ||
            (self.isUserFilterChecked() && self.users().length < 1)
        );
    });

    /**
     * Handles dropdown selection logic for entity and action filters
     *
     * This function manages the complex interaction between the dropdown UI component,
     * the "Select All" option, and the Knockout observable arrays that track selections.
     * It handles various selection scenarios including selecting all options, clearing
     * selections, and partial selections.
     *
     * @param {Object} options - Configuration object for dropdown selection handling
     * @param {ko.observable} options.selectAllObservable - Observable that tracks if "Select All" is checked
     * @param {Array} options.availableOptions - Complete list of available options for the dropdown
     * @param {ko.observableArray} options.selectedOptionsObservable - Knockout observable array tracking selected values
     * @param {HTMLElement} options.dropdownElement - The dropdown DOM element
     * @param {Array} options.selectedGuxOptions - Currently selected options from the GUX dropdown component
     *
     */
    const handleDropdownSelection = function (options) {
        if (self.isFlareThemeEnabled) {
            return;
        }

        const {
            selectAllObservable,
            availableOptions,
            selectedOptionsObservable,
            dropdownElement,
            selectedGuxOptions,
        } = options;

        const hasSelectAll =
            selectedGuxOptions?.includes("select_all") || false;

        const hasAllGuxOptions =
            selectedGuxOptions?.length === availableOptions?.length;
        const hasAllItems =
            selectedOptionsObservable().length === availableOptions.length;

        // Define common operations
        const selectAllItems = () => {
            dropdownElement.value = availableOptions.join();
            selectedOptionsObservable(availableOptions);
            // Using requestAnimationFrame to address visual rendering issue while setting value for selected property.
            requestAnimationFrame(() => {
                selectAllObservable(true);
            });
        };

        const clearSelection = () => {
            setElementProperties(dropdownElement, "value", "");
            selectedOptionsObservable([]);
            selectAllObservable(false);
        };

        const updateSelection = (items, checked = false) => {
            selectedOptionsObservable(items);
            // Using requestAnimationFrame to address visual rendering issue while setting value for selected property.
            requestAnimationFrame(() => {
                selectAllObservable(checked);
            });
        };

        // Handle different scenarios
        if (hasSelectAll && !selectAllObservable()) {
            selectAllItems();
        } else if (hasSelectAll && hasAllItems && selectAllObservable()) {
            clearSelection();
        } else if (
            !hasSelectAll &&
            !selectAllObservable() &&
            hasAllGuxOptions
        ) {
            updateSelection(selectedGuxOptions, true);
        } else {
            updateSelection(selectedGuxOptions, false);
        }
    };

    const entities = Object.freeze({
        ANNOTATION: "Annotation",
        CALIBRATION: "Calibration",
        EVALUATION: "Evaluation",
        RECORDING: "Recording",
        SCREEN_RECORDING: "ScreenRecording",
        SNIPPET_RECORDING: "SnippetRecording",
        SCREEN_MONITORING: "ScreenMonitoring",
        SURVEY: "Survey",
    });

    // Helper function to get available entities based on feature toggles
    function getAvailableEntities() {
        const availableEntities = [
            entities.ANNOTATION,
            entities.CALIBRATION,
            entities.EVALUATION,
            entities.RECORDING,
            entities.SCREEN_RECORDING,
            entities.SURVEY,
        ];

        try {
            // Add snippet recording only when feature toggle is enabled
            if (
                featureToggleService.isEnabled(
                    FeatureToggle.WEM_118_SNIPPET_RECORDING
                )
            ) {
                availableEntities.push(entities.SNIPPET_RECORDING);
            }
            // Add screen monitoring only when feature toggle is enabled
            if (
                featureToggleService.isEnabled(
                    FeatureToggle.WEM_437_SCREEN_MONITORING
                )
            ) {
                availableEntities.push(entities.SCREEN_MONITORING);
            }
        } catch (error) {
            LOG.warn(
                "Feature toggle service not available, showing base entities:",
                error
            );
        }

        return availableEntities;
    }

    self.configEntityDropdown = function (element) {
        self.entityDropdown = element;

        //Initializing the entity types
        const availableEntities = getAvailableEntities();

        self.entityTypes(
            availableEntities.map((entity) => ({
                id: entity,
                label: ko.i18n.renderString(
                    "interactions.audit.entityType." + entity
                ),
                filtered: ko.observable(false),
            }))
        );

        // Set selected entities to all available entities for initial load
        // This ensures the dropdown shows all entities and avoid duplicate
        self.selectedEntities(availableEntities);

        setElementProperties(
            self.entityDropdown,
            "value",
            availableEntities.join()
        );

        if (!self.isFlareThemeEnabled) {
            // Workaround for checking Select All option onInitialLoad
            self.entityExpandedHandler = () =>
                self.entitySelectAll(
                    self.selectedEntities().length === self.entityTypes().length
                );
            self.entityDropdown?.addEventListener(
                "guxexpanded",
                self.entityExpandedHandler,
                { once: true }
            );
        }

        // Update the actions
        self.configureActions();
    };

    self.onEntitySelectionChange = function (data, event) {
        event?.target?.getSelectedValues()?.then((result) => {
            const availableEntities = getAvailableEntities();

            if (self.isFlareThemeEnabled) {
                self.selectedEntities(result);
                self.configureActions();
            } else {
                const options = {
                    selectAllObservable: self.entitySelectAll,
                    availableOptions: availableEntities,
                    selectedOptionsObservable: self.selectedEntities,
                    dropdownElement: self.entityDropdown,
                    selectedGuxOptions: result,
                };

                handleDropdownSelection(options);
                self.configureActions();
            }
        });
    };

    const RecordingActions = Object.freeze([
        "Create",
        "Read",
        "Download",
        "Delete",
        "RestoreRequest",
        "RestoreComplete",
        "UpdateRetention",
        "ApplyProtection",
        "RevokeProtection",
        "Abandon",
        "Archive",
        "Export",
        "Update",
    ]);
    const ScreenRecordingActions = Object.freeze([
        "Create",
        "Read",
        "Download",
        "Delete",
        "RestoreRequest",
        "RestoreComplete",
        "UpdateRetention",
        "Archive",
        "Export",
        "Update",
    ]);
    const SnippetRecordingActions = Object.freeze([
        "Create",
        "Read",
        "Download",
        "Delete",
        "RestoreRequest",
        "RestoreComplete",
        "UpdateRetention",
        "Archive",
        "Export",
    ]);
    const ScreenMonitoringActions = Object.freeze(["Start", "Stop"]);
    const OtherActions = Object.freeze(["Create", "Read", "Delete", "Update"]);

    self.configActionDropdown = function (element) {
        self.actionDropdown = element;
    };

    self.onActionSelectionChange = function (data, event) {
        event?.target?.getSelectedValues()?.then((result) => {
            if (self.isFlareThemeEnabled) {
                self.selectedActions(result);
            } else {
                const options = {
                    selectAllObservable: self.actionSelectAll,
                    availableOptions: self
                        .actionTypes()
                        .map((action) => action.id),
                    selectedOptionsObservable: self.selectedActions,
                    dropdownElement: self.actionDropdown,
                    selectedGuxOptions: result,
                };
                handleDropdownSelection(options);
            }
        });
    };

    self.configureActions = function () {
        self.selectedActions([]);
        if (!self.isFlareThemeEnabled) {
            self.actionSelectAll(false);
        }
        setElementProperties(self.actionDropdown, "value", "");
        const actions = _.uniq(
            _.flatMap(self.selectedEntities(), (entityType) => {
                if (entityType === entities.RECORDING) {
                    return [...RecordingActions];
                }
                if (entityType === entities.SCREEN_RECORDING) {
                    return [...ScreenRecordingActions];
                }
                if (entityType === entities.SNIPPET_RECORDING) {
                    return [...SnippetRecordingActions];
                }
                if (entityType === entities.SCREEN_MONITORING) {
                    return [...ScreenMonitoringActions];
                }
                return [...OtherActions];
            })
        );
        const actionsMap = actions.map((action) => ({
            id: action,
            label: ko.i18n.renderString(`interactions.audit.actions.${action}`),
            filtered: ko.observable(false),
        }));
        self.actionTypes(_.sortBy(actionsMap, ["label"]));
    };

    /**
     * This function handles the filtering logic for both entity type and action type dropdowns
     * by performing case-insensitive comparisons between the input text and dropdown option labels.
     */
    self.dropdownFilter = function (data, event) {
        if (!event || !event.target || event?.detail == null) {
            return;
        }
        const inputText = event.detail.toLowerCase();

        // Helper function to handle filtering for any dropdown type
        const applyFilter = (
            items,
            selectAllObservable,
            selectedItems,
            totalItems
        ) => {
            items.forEach((item) => {
                const itemLabel = item?.label?.toLowerCase() || "";
                item?.filtered(!itemLabel.includes(inputText));
            });

            if (!self.isFlareThemeEnabled) {
                // Maintain "Select All" state when all items are selected
                if (selectedItems.length === totalItems) {
                    // On filtering the select-all option state is being updated, so overriding it here.
                    selectAllObservable(false);
                    requestAnimationFrame(() => {
                        selectAllObservable(true);
                    });
                }
            }
        };

        // Handle filtering for entity dropdown
        if (event.target === self.entityDropdown) {
            applyFilter(
                self.entityTypes(),
                self.entitySelectAll,
                self.selectedEntities(),
                self.entityTypes().length
            );
        }
        // Handle filtering for action dropdown
        else if (event.target === self.actionDropdown) {
            applyFilter(
                self.actionTypes(),
                self.actionSelectAll,
                self.selectedActions(),
                self.actionTypes().length
            );
        }
    };

    const COLUMNS = [
        {
            id: "entityId",
            label: "interactions.audit.entityId",
        },
        {
            id: "entityType",
            label: "interactions.audit.entityType",
        },
        {
            id: "mediaSubtype",
            label: "interactions.audit.mediaSubtype",
        },
        {
            id: "action",
            label: "interactions.audit.action",
        },
        {
            id: "level",
            label: "interactions.audit.level",
        },
        {
            id: "user",
            label: "interactions.audit.user",
        },
        {
            id: "trusteeOrgId",
            label: "interactions.audit.trusteeOrgId",
        },
        {
            id: "timestamp",
            label: "interactions.audit.timestamp",
        },
    ];
    self.toggleUserFilterVisibility = function (obj, event) {
        self.isUserFilterChecked(event.target.checked);
        if (!event.target.checked) {
            self.users([]);
        }
        self.showUserFilter(!self.showUserFilter());
    };

    self.toggleTrusteeFilter = function (obj, event) {
        self.isTrusteeFilterChecked(event.target.checked);
    };

    const MAX_RETRIES = 10;
    let transactionId = "";
    const pageSize = 25;
    let cursor = "";
    let cursors = [];

    self.updatePageCursor = function (cursor) {
        if (_.isUndefined(cursor)) {
            self.hasNextPage(false);
            return;
        }
        self.hasNextPage(true);
        cursors[self.currentPageNo()] = cursor;
    };

    self.getAuditQueryResults = function () {
        cursor = cursors[self.currentPageNo() - 1];
        qualityManagementService
            .getAuditQueryResults(transactionId, cursor, pageSize)
            .then(function (response) {
                const rows = _.map(response.entities, function (auditData) {
                    let userName = "";
                    const mediaSubtype = auditData?.context?.MediaSubtype;
                    if (auditData.user && auditData.user.id) {
                        userName = auditData.user.name;

                        if (
                            _.isUndefined(userName) &&
                            !_.isUndefined(auditData.userTrusteeOrgId)
                        ) {
                            userName = ko.i18n.renderString(
                                "auditView.label.trusteeUser"
                            );
                        }
                    }

                    return {
                        timestamp: ko.i18n.formatDate(
                            auditData.eventDate,
                            ko.i18n.renderString(
                                "interactions.audit.timeFormat"
                            ),
                            null,
                            FULL_DATE_SHORT_TIME_OPTIONS
                        ),
                        action: ko.i18n.renderString(
                            "interactions.audit.actions." + auditData.action
                        ),
                        level: ko.i18n.renderString(
                            "interactions.audit.level." + auditData.level
                        ),
                        entityType: ko.i18n.renderString(
                            "interactions.audit.entityType." +
                                auditData.entityType
                        ),
                        user: userName,
                        trusteeOrgId: auditData.userTrusteeOrgId,
                        entityId: auditData.entity.id,
                        mediaSubtype: mediaSubtype
                            ? ko.i18n.renderString(
                                  `interactions.audit.mediaSubtype.${mediaSubtype}`
                              )
                            : "",
                    };
                });
                self.auditGrid().rows(rows);
                self.updatePageCursor(response.cursor);
                self.showLoadingSpinner(false);
                self.showDataTable(true);
                return null;
            })
            .catch(function (err) {
                self.showLoadingSpinner(false);
                self.showDataTable(false);
                self.showLoadAllAudit(true);
                errorCallbackFactory.createCallback({
                    namespace: "interactions.audit.error.default",
                })(err);
            })
            .finally(function () {
                self.auditGrid().loading(false);
            });
    };

    self.onPageNumberChange = function (pageNumber) {
        self.currentPageNo(pageNumber);
        self.getAuditQueryResults();
    };

    function pollFunction(callback) {
        return withRequestRetry(
            callback,
            function (response) {
                if (
                    response.state === "Failed" ||
                    response.state === "Cancelled"
                ) {
                    throw new Error("Audit query failed or cancelled");
                }

                return (
                    response.state === "Queued" || response.state === "Running"
                );
            },
            function (error) {
                self.showLoadingSpinner(false);
                errorCallbackFactory.createCallback({
                    namespace: "interactions.audit.error.default",
                })(error);
            },
            { maxRetries: MAX_RETRIES, startIntervalMs: 3000 }
        )();
    }

    self.queryForAudits = function (startDate, endDate, filters) {
        self.showDataTable(false);
        self.showLoadingSpinner(true);
        const interval = startDate + "/" + endDate;
        return qualityManagementService
            .createAuditQuery(interval, filters, SORTING_ORDER)
            .then(function (response) {
                transactionId = response.id;
                cursors = [];
                self.currentPageNo(1);
                const getAuditQueryStatusForTransaction = () =>
                    qualityManagementService.getAuditQueryStatus(transactionId);
                return pollFunction(getAuditQueryStatusForTransaction);
            })
            .then(() => {
                self.getAuditQueryResults();
                return null;
            })
            .catch(function (err) {
                self.showLoadingSpinner(false);
                self.showDataTable(false);
                self.showLoadAllAudit(true);
                errorCallbackFactory.createCallback({
                    namespace: "interactions.audit.error.default",
                })(err);
            });
    };

    const filterItem = function (property, value) {
        return {
            property: property,
            value: value,
        };
    };

    self.loadAllAudits = function () {
        self.showLoadAllAudit(false);

        const filters = [];
        filters.push(
            filterItem(
                filterProperties.CONVERSATION_ID,
                self.conversation().id ? self.conversation().id : conversationId
            )
        );

        let startTime = self.conversation() && self.conversation().startTime;
        if (!startTime && self.recording()) {
            startTime = ko.unwrap(self.recording().startTime);
        }
        self.queryForAudits(startTime, moment.utc().toISOString(), filters);
    };

    self.getFilteredAudits = function () {
        self.showLoadAllAudit(false);
        const userList = [];
        const filters = [];

        filters.push(
            filterItem(
                filterProperties.CONVERSATION_ID,
                self.conversation().id ? self.conversation().id : conversationId
            )
        );

        if (
            !_.isUndefined(self.selectedEntities()) &&
            self.selectedEntities().length > 0
        ) {
            filters.push(
                filterItem(
                    filterProperties.ENTITY_TYPE,
                    self.selectedEntities().join()
                )
            );
        }
        if (
            !_.isUndefined(self.selectedActions()) &&
            self.selectedActions().length > 0
        ) {
            filters.push(
                filterItem(
                    filterProperties.ACTION,
                    self.selectedActions().join()
                )
            );
        }
        if (
            self.isUserFilterChecked() &&
            !_.isUndefined(self.users()) &&
            self.users().length > 0
        ) {
            self.users().forEach(function (user) {
                userList.push(user.id);
            });
            filters.push(filterItem(filterProperties.USER_ID, userList.join()));
        }
        if (self.isTrusteeFilterChecked()) {
            filters.push(
                filterItem(
                    filterProperties.TRUSTEE_ONLY_AUDITS,
                    self.isTrusteeFilterChecked()
                )
            );
        }

        let startTime = self.conversation() && self.conversation().startTime;
        if (!startTime && self.recording()) {
            startTime = ko.unwrap(self.recording().startTime);
        }
        self.queryForAudits(startTime, moment.utc().toISOString(), filters);
    };

    self.loadPreviousPage = function () {
        self.currentPageNo(self.currentPageNo() - 1);
        self.onPageNumberChange(self.currentPageNo());
    };

    self.loadNextPage = function () {
        self.hasNextPage(false);
        self.currentPageNo(self.currentPageNo() + 1);
        self.onPageNumberChange(self.currentPageNo());
    };

    self.auditGrid({
        columns: COLUMNS,
        rows: ko.observableArray([]),
        loading: ko.observable(true),
    });

    self.onDispose = function () {
        if (!self.isFlareThemeEnabled) {
            self.entityDropdown?.removeEventListener(
                "guxexpanded",
                self.entityExpandedHandler
            );
        }
    };
});

export default AuditView;
